class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.querySelector('form.search').addEventListener('submit', this.onFormSubmit.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));
    this.querySelector('button[type="reset"]').addEventListener('click', this.clear.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clear();
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }
  
  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  } 

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear(event);
      this.close(true);
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
     
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      /*
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      */
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = ''; 

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
 
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    const perPage = this.dataset.perPage || 4;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal) this.modal.classList.add('searching');
      return;
    }

    // Check if search term looks like a SKU (contains numbers/letters mix, or common SKU patterns)
    const isLikelySKU = this.isLikelySKU(searchTerm);

    if (isLikelySKU) {
      // Use combined approach: get both SKU-specific and regular results
      this.getCombinedSearchResults(searchTerm, queryKey, perPage);
    } else {
      // Use regular predictive search
      this.getRegularSearchResults(searchTerm, queryKey, perPage);
    }
  }

  isLikelySKU(searchTerm) {
    // Check if term looks like a SKU - contains mix of letters and numbers, or common SKU patterns
    const hasLettersAndNumbers = /[a-zA-Z]/.test(searchTerm) && /[0-9]/.test(searchTerm);
    const isAlphanumeric = /^[a-zA-Z0-9-_\.]+$/.test(searchTerm);
    const isShortAndSpecific = searchTerm.length >= 3 && searchTerm.length <= 20;
    
    return hasLettersAndNumbers && isAlphanumeric && isShortAndSpecific;
  }

  getCombinedSearchResults(searchTerm, queryKey, perPage) {
    let type = '';
    if (this.querySelector('input[name="resources[type]"]') && this.querySelector('input[name="resources[type]"]').value) {
      type = `&${encodeURIComponent('resources[type]')}=${this.querySelector('input[name="resources[type]"]').value}`;
    }

    // Make both requests - regular predictive search AND SKU-specific search
    const regularSearchUrl = `${theme.routes.search_url}?q=${encodeURIComponent(searchTerm)}&type=product&options[prefix]=last`;
    const skuSearchUrl = `${theme.routes.search_url}?q=sku:${encodeURIComponent(searchTerm)}&type=product&options[prefix]=last`;

    Promise.all([
      fetch(regularSearchUrl).then(r => r.text()),
      fetch(skuSearchUrl).then(r => r.text())
    ])
    .then(([regularHtml, skuHtml]) => {
      // Parse both results and combine them
      const regularDoc = new DOMParser().parseFromString(regularHtml, 'text/html');
      const skuDoc = new DOMParser().parseFromString(skuHtml, 'text/html');
      
      // Extract products from both results
      const regularProducts = this.extractProductsFromSearchResults(regularDoc);
      const skuProducts = this.extractProductsFromSearchResults(skuDoc);
      
      // Combine and deduplicate products (SKU results first)
      const combinedProducts = this.combineProducts(skuProducts, regularProducts);
      
      // Create predictive search format HTML
      const resultsMarkup = this.createPredictiveSearchMarkup(combinedProducts, searchTerm, perPage);
      
      // Cache and render results
      this.allPredictiveSearchInstances.forEach(
        (predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        }
      );
      
      this.renderSearchResults(resultsMarkup);
      if (this.modal) this.modal.classList.add('searching');
    })
    .catch((error) => {
      console.warn('SKU search failed, falling back to regular search:', error);
      this.getRegularSearchResults(searchTerm, queryKey, perPage);
    });
  }

  getRegularSearchResults(searchTerm, queryKey, perPage) {
    let type = '';
    if (this.querySelector('input[name="resources[type]"]') && this.querySelector('input[name="resources[type]"]').value) {
      type = `&${encodeURIComponent('resources[type]')}=${this.querySelector('input[name="resources[type]"]').value}`;
    }
    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[limit]')}=${perPage}&${encodeURIComponent('resources[limit_scope]')}=each&section_id=predictive-search&${encodeURIComponent('resources[options][fields]')}=title,variants.sku,product_type,variants.title${type}`)
      .then((response) => { 
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
        
        if (this.modal) this.modal.classList.add('searching');
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        
        this.close();
        throw error;
      }); 
  }

  extractProductsFromSearchResults(doc) {
    const products = [];
    const productElements = doc.querySelectorAll('.card-wrapper');
    
    productElements.forEach((element, index) => {
      if (index >= 4) return; // Limit to 4 products like predictive search
      
      const linkElement = element.querySelector('a.full-unstyled-link');
      const titleElement = element.querySelector('.card__heading a, .card-information__text a');
      const imageElement = element.querySelector('.card__media img, .media img');
      const priceElement = element.querySelector('.price');
      const vendorElement = element.querySelector('.card__information .caption-with-letter-spacing');
      
      if (linkElement && titleElement) {
        products.push({
          url: linkElement.href,
          title: titleElement.textContent.trim(),
          image: imageElement ? {
            src: imageElement.src,
            alt: imageElement.alt,
            width: imageElement.width || 70,
            height: imageElement.height || 70
          } : null,
          price: priceElement ? priceElement.innerHTML : '',
          vendor: vendorElement ? vendorElement.textContent.trim() : ''
        });
      }
    });
    
    return products;
  }

  combineProducts(skuProducts, regularProducts) {
    const seen = new Set();
    const combined = [];
    
    // Add SKU products first (higher priority)
    skuProducts.forEach(product => {
      if (!seen.has(product.url)) {
        seen.add(product.url);
        combined.push(product);
      }
    });
    
    // Add regular products that weren't already included
    regularProducts.forEach(product => {
      if (!seen.has(product.url) && combined.length < 4) {
        seen.add(product.url);
        combined.push(product);
      }
    });
    
    return combined.slice(0, 4); // Limit to 4 products
  }

  createPredictiveSearchMarkup(products, searchTerm, perPage) {
    if (products.length === 0) {
      return `
        <div id="predictive-search__results-list" class="predictive-search-results is-empty" role="listbox">
          <div class="title-wrapper center">
            <p class="title h3 title--primary">No results found for "${searchTerm}"</p>
            <p class="typeset">Check the spelling or try a different search term.</p>
          </div>
          <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
            <button class="predictive-search__item predictive-search__item--term button button--small button--full-width" tabindex="-1" role="option" aria-selected="false">
              <span data-predictive-search-search-for-text class="text">Search for "${searchTerm}"</span>
            </button>
          </div>
        </div>
      `;
    }

    const productResults = products.map((product, index) => {
      const imageHtml = product.image ? `
        <div>
          <span class="predictive-search__image media-wrapper media-wrapper--small">
            <div class="media media--adapt" style="--image-ratio-percent: 100%;">
              <img
                src="${product.image.src}"
                alt="${product.image.alt}"
                width="${product.image.width}"
                height="${product.image.height}"
                loading="lazy"
                is="lazy-image" />
            </div>
          </span>
        </div>
      ` : '';

      const vendorHtml = product.vendor ? `
        <span class="visually-hidden">Vendor</span>
        <div class="predictive-search__item-vendor card-article-info caption-with-letter-spacing">${product.vendor}</div>
      ` : '';

      return `
        <li id="predictive-search-option-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="${product.url}" class="predictive-search__item predictive-search__item--link link link--text" tabindex="-1">
            ${imageHtml}
            <div class="predictive-search__item-content">
              ${vendorHtml}
              <span class="predictive-search__item-heading h4">${product.title}</span>
              ${product.price}
            </div>
          </a>
        </li>
      `;
    }).join('');

    return `
      <div id="predictive-search__results-list" class="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
          <div class="predictive-search__result-group">
            <div>
              <h2 id="predictive-search-products" class="predictive-search__heading h4">Products</h2>
              <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
                ${productResults}
              </ul>
            </div>
          </div>
        </div>
        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <button class="predictive-search__item predictive-search__item--term button button--small button--full-width" tabindex="-1" role="option" aria-selected="false">
            <span data-predictive-search-search-for-text class="text">Search for "${searchTerm}"</span>
          </button>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value">
          ${products.length > 0 ? `${products.length} results found: ${products.length} products` : `No results found for ${searchTerm}`}
        </span>
      </div>
    `;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);  

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() { 
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  
  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    document.body.classList.remove('predictive-search--focus');
  }

  clear(event) {
    event.preventDefault();
    
    this.input.value = '';
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.input.focus();

    if (this.modal) this.modal.classList.remove('searching');
  }
}

customElements.define('predictive-search', PredictiveSearch);
